import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
    Input,
    InputNumber,
    Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Upload,
  message,
} from "antd";
import {
  CheckOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  PlusOutlined,
  RollbackOutlined,
  SendOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import useDiscipline from "../../../hooks/academics/useDiscipline";

const CASE_TYPES = [
  { value: "student", label: "Student" },
  { value: "staff", label: "Staff" },
];

const SEVERITIES = ["low", "medium", "high", "critical"];
const CASE_STATUSES = [
  "open",
  "under_review",
  "escalated",
  "resolved",
  "closed",
  "appealed",
];
const ROLES = [
  "teacher",
  "class_coordinator",
  "head_of_department",
  "head_of_section",
  "deputy_headteacher",
  "headteacher",
  "principal",
];
const SUBMIT_TO_ROLES = [
  {
    value: "class_coordinator",
    label: "Class Coordinator",
    roleName: "Class Coordinator",
  },
  {
    value: "head_of_department",
    label: "Head of Department (HOD)",
    roleName: "Head of Department (HOD)",
  },
];
const SANCTION_TYPES = [
  "verbal_warning",
  "written_warning",
  "parent_call",
  "detention",
  "suspension",
  "expulsion",
  "counselling",
  "appraisal_note",
  "demotion",
  "dismissal",
];

const statusColor = (status) =>
  ({
    open: "blue",
    under_review: "gold",
    escalated: "orange",
    resolved: "green",
    closed: "default",
    appealed: "purple",
    low: "green",
    medium: "gold",
    high: "orange",
    critical: "red",
  })[status] || "default";

const titleCase = (value = "") =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const DisciplineManagement = () => {
  const {
    loading,
    fetchCases,
    fetchCaseDetails,
    saveCase,
    submitCase: submitCaseRequest,
    sendBackCase,
    bulkEscalateCases,
    bulkCloseCases,
    createEscalationStep,
    updateEscalationStep,
    resolveEscalationStep,
    uploadEvidence,
    createSanction,
    bulkApproveSanctions,
    bulkLockSanctions,
    fetchSeverityDeductions,
    saveSeverityDeductions,
  } = useDiscipline();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [cases, setCases] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [escalationModalOpen, setEscalationModalOpen] = useState(false);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [sanctionModalOpen, setSanctionModalOpen] = useState(false);
  const [sendBackModalOpen, setSendBackModalOpen] = useState(false);
  const [bulkEscalateOpen, setBulkEscalateOpen] = useState(false);
  const [escalationView, setEscalationView] = useState(null);
  const [escalationEditStep, setEscalationEditStep] = useState(null);
  const [submitModalRecord, setSubmitModalRecord] = useState(null);
  const [submitHandlers, setSubmitHandlers] = useState([]);
  const [submitHandlersLoading, setSubmitHandlersLoading] = useState(false);
  const [caseForm] = Form.useForm();
  const [escalationForm] = Form.useForm();
  const [escalationEditForm] = Form.useForm();
  const [submitForm] = Form.useForm();
  const [evidenceForm] = Form.useForm();
  const [sanctionForm] = Form.useForm();
  const [sendBackForm] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [closeSeverityForm] = Form.useForm();
  const [severityForm] = Form.useForm();
  const [severityModalOpen, setSeverityModalOpen] = useState(false);
  const [severityMap, setSeverityMap] = useState(null);
  const [severitySource, setSeveritySource] = useState(null);
  const [severityLoading, setSeverityLoading] = useState(false);
  const [severityError, setSeverityError] = useState(null);
  const [closeSeverityCases, setCloseSeverityCases] = useState([]);

  const selectedIds = selectedRowKeys.map((key) => Number(key));
  const caseTypeValue = Form.useWatch("caseType", caseForm);
  const statusValue = Form.useWatch("status", caseForm);
  const isTerminalStatus = ["resolved", "closed"].includes(statusValue);
  const isClosedCaseEdit = ["resolved", "closed"].includes(
    selectedCase?.status,
  );
  const caseIsLocked = ["resolved", "closed"].includes(selectedCase?.status);

  const userName = (user) =>
    user.full_name ||
    user.fullName ||
    [user.first_name || user.firstName, user.last_name || user.lastName]
      .filter(Boolean)
      .join(" ") ||
    user.username ||
    user.email ||
    `User ${user.userID || user.id}`;

  const userId = (user) => user.userID || user.id;

  const studentName = (student) =>
    student.fullName ||
    student.studentName ||
    [
      student.firstName || student.first_name,
      student.middleName || student.middle_name,
      student.lastName || student.last_name,
    ]
      .filter(Boolean)
      .join(" ") ||
    student.name ||
    `Student ${student.id}`;

  const fetchBootstrap = useCallback(async () => {
    const [classRes, streamRes, staffRes, studentRes] = await Promise.all([
      Utilities.fetchWithAuth("GET", Settings.urls.bootstrap.classes),
      Utilities.fetchWithAuth("GET", Settings.urls.bootstrap.stream),
      Utilities.fetchWithAuth("GET", Settings.urls.auth.listStaff),
      Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.academics.studentSearch}?page_size=500`,
      ),
    ]);
    if (classRes?.ok)
      setClasses(
        Array.isArray(classRes.data)
          ? classRes.data
          : classRes.data?.results || [],
      );
    if (streamRes?.ok)
      setStreams(
        Array.isArray(streamRes.data)
          ? streamRes.data
          : streamRes.data?.results || [],
      );
    if (staffRes?.ok)
      setStaff(
        Array.isArray(staffRes.data)
          ? staffRes.data
          : staffRes.data?.results || [],
      );
    if (studentRes?.ok)
      setStudents(
        Array.isArray(studentRes.data)
          ? studentRes.data
          : studentRes.data?.results || [],
      );
  }, []);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    fetchBootstrap();
  }, [fetchAcademicYears, fetchTerms, fetchBootstrap]);

  const load = useCallback(async () => {
    setSelectedRowKeys([]);
    setCases((await fetchCases(filters)) || []);
  }, [fetchCases, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openCaseModal = (record = null) => {
    setSelectedCase(record);
    caseForm.setFieldsValue(
      record
        ? {
            ...record,
            subjectStudentID:
              record.subjectStudentID != null
                ? String(record.subjectStudentID)
                : undefined,
            incidentDate: record.incidentDate
              ? dayjs(record.incidentDate)
              : null,
          }
        : {
            caseType: "student",
            severity: "low",
            status: "open",
            incidentDate: dayjs(),
          },
    );
    setCaseModalOpen(true);
  };

  const submitCase = async () => {
    const values = await caseForm.validateFields();
    const payload = {
      ...values,
      incidentDate: values.incidentDate?.format("YYYY-MM-DD"),
    };
    if (payload.caseType === "student") {
      payload.subjectStaffID = null;
    }
    if (payload.caseType === "staff") {
      payload.subjectStudentID = null;
    }
    const result = await saveCase(payload, selectedCase?.id);
    if (result) {
      setCaseModalOpen(false);
      caseForm.resetFields();
      load();
    }
  };

  const openSubmitModal = (record) => {
    setSubmitModalRecord(record);
    submitForm.resetFields();
    setSubmitHandlers([]);
  };

  const loadSubmitHandlers = async (toRole) => {
    submitForm.setFieldsValue({ handledByID: undefined });
    setSubmitHandlers([]);
    const target = SUBMIT_TO_ROLES.find((role) => role.value === toRole);
    if (!target) return;
    setSubmitHandlersLoading(true);
    const response = await Utilities.fetchWithAuth(
      "GET",
      `${Settings.urls.auth.listStaff}?role_name=${encodeURIComponent(target.roleName)}`,
    );
    if (response?.ok) {
      setSubmitHandlers(
        Array.isArray(response.data)
          ? response.data
          : response.data?.results || [],
      );
    }
    setSubmitHandlersLoading(false);
  };

  const confirmSubmit = async () => {
    const record = submitModalRecord;
    const values = await submitForm.validateFields();
    const result = await submitCaseRequest(record.id, {
      handledByID: values.handledByID,
      toRole: values.toRole,
    });
    if (result) {
      setSubmitModalRecord(null);
      submitForm.resetFields();
      if (selectedCase?.id === record.id) refreshSelectedCase();
      else load();
    }
  };

  const submitSendBack = async () => {
    const values = await sendBackForm.validateFields();
    const result = await sendBackCase(selectedCase.id, values);
    if (result) {
      setSendBackModalOpen(false);
      sendBackForm.resetFields();
      refreshSelectedCase();
    }
  };

  const openDetails = async (record) => {
    const details = await fetchCaseDetails(record.id);
    if (details) {
      setSelectedCase(details);
      setDrawerOpen(true);
    }
  };

  const refreshSelectedCase = async () => {
    if (!selectedCase?.id) return;
    const details = await fetchCaseDetails(selectedCase.id);
    if (details) setSelectedCase(details);
    load();
  };

  const confirmResolveStep = (step) => {
    Modal.confirm({
      title: "Resolve entire case?",
      content:
        "Resolving marks the entire case as resolved and locks it as read-only — this cannot be undone.",
      okText: "Resolve Case",
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await resolveEscalationStep(step.id, {});
        if (result) refreshSelectedCase();
      },
    });
  };

  const submitEscalation = async () => {
    const values = await escalationForm.validateFields();
    const result = await createEscalationStep({
      ...values,
      caseID: selectedCase.id,
    });
    if (result) {
      setEscalationModalOpen(false);
      escalationForm.resetFields();
      refreshSelectedCase();
    }
  };

  const openEscalationEdit = (step) => {
    setEscalationEditStep(step);
    escalationEditForm.setFieldsValue({
      actionTaken: step.actionTaken,
      notes: step.notes,
    });
  };

  const submitEscalationEdit = async () => {
    const values = await escalationEditForm.validateFields();
    const result = await updateEscalationStep(escalationEditStep.id, values);
    if (result) {
      setEscalationEditStep(null);
      escalationEditForm.resetFields();
      refreshSelectedCase();
    }
  };

  const submitEvidence = async () => {
    const values = await evidenceForm.validateFields();
    const file = values.file?.[0]?.originFileObj;
    if (!file) {
      message.warning("Select a file");
      return;
    }
    const data = new FormData();
    data.append("caseID", selectedCase.id);
    data.append("title", values.title);
    data.append("file", file);
    if (values.notes) data.append("notes", values.notes);
    const result = await uploadEvidence(data);
    if (result) {
      setEvidenceModalOpen(false);
      evidenceForm.resetFields();
      refreshSelectedCase();
    }
  };

  const submitSanction = async () => {
    const values = await sanctionForm.validateFields();
    const payload = {
      ...values,
      caseID: selectedCase.id,
      effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD"),
    };
    const result = await createSanction(payload);
    if (result) {
      setSanctionModalOpen(false);
      sanctionForm.resetFields();
      refreshSelectedCase();
    }
  };

  const confirmBulkEscalate = async () => {
    if (!selectedIds.length) {
      message.warning("Select at least one case");
      return;
    }
    const values = await bulkForm.validateFields();
    const result = await bulkEscalateCases(
      selectedIds,
      values.toStatus,
      values.notes,
    );
    if (result) {
      setBulkEscalateOpen(false);
      bulkForm.resetFields();
      load();
    }
  };

  const closeSelectedCases = async () => {
    if (!selectedIds.length) {
      message.warning("Select at least one resolved case");
      return;
    }
    const targets = cases.filter((c) => selectedIds.includes(Number(c.id)));
    if (!targets.length) {
      message.warning("Select at least one resolved case");
      return;
    }
    closeSeverityForm.resetFields();
    setCloseSeverityCases(targets);
  };

  const submitCloseSeverities = async () => {
    const values = await closeSeverityForm.validateFields();
    const severities = {};
    closeSeverityCases.forEach((c) => {
      const level = values[`severity_${c.id}`];
      if (level) severities[c.id] = level;
    });
    const result = await bulkCloseCases(
      closeSeverityCases.map((c) => c.id),
      severities,
    );
    if (!result) return;
    if (result.ok) {
      setCloseSeverityCases([]);
      load();
      return;
    }
    if (Array.isArray(result.missing) && result.missing.length) {
      const missingSet = new Set(result.missing.map((id) => Number(id)));
      setCloseSeverityCases((prev) => prev.filter((c) => missingSet.has(Number(c.id))));
    }
  };

  const loadSeverityMap = async () => {
    setSeverityLoading(true);
    setSeverityError(null);
    try {
      const data = await fetchSeverityDeductions();
      if (data) {
        setSeverityMap(data.deductions || {});
        setSeveritySource(data.source || null);
        severityForm.setFieldsValue(data.deductions || {});
      }
    } catch (error) {
      setSeverityError(error?.message || "Failed to load severity deductions");
    } finally {
      setSeverityLoading(false);
    }
  };

  const openSeverityMap = () => {
    setSeverityModalOpen(true);
    loadSeverityMap();
  };

  const submitSeverityMap = async () => {
    try {
      const values = await severityForm.validateFields();
      const result = await saveSeverityDeductions(values);
      if (result) loadSeverityMap();
    } catch {
      /* validation errors render inline */
    }
  };

  const evidenceUploadValue = (event) =>
    Array.isArray(event) ? event : event?.fileList;

  const searchStudents = useMemo(() => {
    let timer;
    return (term) => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const response = await Utilities.fetchWithAuth(
          "GET",
          `${Settings.urls.academics.studentSearch}?page_size=50${term ? `&search=${encodeURIComponent(term)}` : ""}`,
        );
        if (response?.ok)
          setStudents(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
      }, 350);
    };
  }, []);

  const studentOptions = useMemo(() => {
    const options = students.map((student) => ({
      value: String(student.id),
      label: `${studentName(student)}${student.spc ? ` (${student.spc})` : ""}`,
    }));
    const selectedId = selectedCase?.subjectStudentID;
    if (
      selectedId != null &&
      selectedCase?.subjectStudentName &&
      !options.some((option) => option.value === String(selectedId))
    ) {
      options.unshift({
        value: String(selectedId),
        label: selectedCase.subjectStudentName,
      });
    }
    return options;
  }, [students, selectedCase]); // eslint-disable-line react-hooks/exhaustive-deps

  const caseColumns = [
    { title: "Case #", dataIndex: "caseNumber", width: 140 },
    { title: "Title", dataIndex: "title", width: 220 },
    {
      title: "Subject",
      width: 180,
      render: (_, record) =>
        record.subjectStudentName || record.subjectStaffName || "-",
    },
    {
      title: "Type",
      dataIndex: "caseType",
      width: 100,
      render: (v) => <Tag>{titleCase(v)}</Tag>,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      width: 110,
      render: (v) => <Tag color={statusColor(v)}>{titleCase(v)}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (v) => <Tag color={statusColor(v)}>{titleCase(v)}</Tag>,
    },
    {
      title: "Class",
      dataIndex: "className",
      width: 110,
      render: (v) => v || "-",
    },
    { title: "Incident Date", dataIndex: "incidentDate", width: 130 },
    {
      title: "Reporter",
      dataIndex: "reportedByName",
      width: 150,
      render: (v) => v || "-",
    },
    {
      title: "Actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => openDetails(record)}
          />
          {!record.isSubmitted && (
            <Button type="link" onClick={() => openCaseModal(record)}>
              Edit
            </Button>
          )}
          {record.status === "open" && !record.isSubmitted && (
            <Button
              type="link"
              icon={<SendOutlined />}
              onClick={() => openSubmitModal(record)}
            >
              Submit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const approveSanction = async (id) => {
    const result = await bulkApproveSanctions([id]);
    if (result) refreshSelectedCase();
  };

  const sanctionColumns = [
    { title: "Type", dataIndex: "sanctionType", render: (v) => titleCase(v) },
    { title: "Description", dataIndex: "description" },
    {
      title: "Approved",
      dataIndex: "isApproved",
      render: (v) => (
        <Tag color={v ? "green" : "gold"}>{v ? "Approved" : "Pending"}</Tag>
      ),
    },
    {
      title: "Locked",
      dataIndex: "locked",
      render: (v) => (
        <Tag color={v ? "default" : "blue"}>{v ? "Locked" : "Open"}</Tag>
      ),
    },
    {
      title: "Actions",
      render: (_, row) =>
        !caseIsLocked && !row.isApproved && !row.locked ? (
          <Button
            size="small"
            type="link"
            icon={<CheckOutlined />}
            onClick={() => approveSanction(row.id)}
          >
            Approve
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0 }}>Discipline Management</h4>
        <Space>
          <Button icon={<SyncOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openCaseModal()}
          >
            New Case
          </Button>
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search
            placeholder="Search cases"
            allowClear
            style={{ width: 220 }}
            onSearch={(search) => setFilters((prev) => ({ ...prev, search }))}
          />
          <Select
            placeholder="Academic Year"
            allowClear
            style={{ width: 170 }}
            onChange={(acadyrID) =>
              setFilters((prev) => ({ ...prev, acadyrID }))
            }
            options={academicYears.map((y) => ({
              value: y.id,
              label: y.acadyr,
            }))}
          />
          <Select
            placeholder="Term"
            allowClear
            style={{ width: 140 }}
            onChange={(termID) => setFilters((prev) => ({ ...prev, termID }))}
            options={terms.map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            placeholder="Class"
            allowClear
            style={{ width: 150 }}
            onChange={(classID) => setFilters((prev) => ({ ...prev, classID }))}
            options={classes.map((c) => ({
              value: c.id,
              label: c.name || c.className,
            }))}
          />
          <Select
            placeholder="Type"
            allowClear
            style={{ width: 130 }}
            onChange={(caseType) =>
              setFilters((prev) => ({ ...prev, caseType }))
            }
            options={CASE_TYPES}
          />
          <Select
            placeholder="Severity"
            allowClear
            style={{ width: 140 }}
            onChange={(severity) =>
              setFilters((prev) => ({ ...prev, severity }))
            }
            options={SEVERITIES.map((s) => ({ value: s, label: titleCase(s) }))}
          />
          <Select
            placeholder="Status"
            allowClear
            style={{ width: 150 }}
            onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
            options={CASE_STATUSES.map((s) => ({
              value: s,
              label: titleCase(s),
            }))}
          />
        </Space>
      </Card>

      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Button onClick={() => setBulkEscalateOpen(true)}>
            Bulk Escalate
          </Button>
          <Button icon={<CheckOutlined />} onClick={closeSelectedCases}>
            Close Resolved
          </Button>
          <Button onClick={openSeverityMap}>Severity deductions</Button>
        </Space>
        <Table
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          columns={caseColumns}
          dataSource={cases}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Modal
        title={
          selectedCase?.id ? "Edit Discipline Case" : "New Discipline Case"
        }
        open={caseModalOpen}
        onOk={submitCase}
        onCancel={() => setCaseModalOpen(false)}
        width={720}
        destroyOnClose
      >
        <Form form={caseForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="caseType"
                label="Case Type"
                rules={[{ required: true }]}
              >
                <Select options={CASE_TYPES} disabled={isClosedCaseEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="incidentDate"
                label="Incident Date"
                rules={[{ required: true }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabled={isClosedCaseEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true }]}
              >
                <Input disabled={isClosedCaseEdit} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={4} disabled={isClosedCaseEdit} />
              </Form.Item>
            </Col>
            {caseTypeValue !== "staff" && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="subjectStudentID"
                  label="Student"
                  rules={
                    caseTypeValue === "student" ? [{ required: true }] : []
                  }
                >
                  <Select
                    showSearch
                    allowClear
                    disabled={isClosedCaseEdit}
                    placeholder="Search student by name"
                    filterOption={false}
                    onSearch={searchStudents}
                    options={studentOptions}
                  />
                </Form.Item>
              </Col>
            )}
            {caseTypeValue !== "student" && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="subjectStaffID"
                  label="Staff"
                  rules={caseTypeValue === "staff" ? [{ required: true }] : []}
                >
                  <Select
                    showSearch
                    allowClear
                    disabled={isClosedCaseEdit}
                    placeholder="Select staff"
                    optionFilterProp="label"
                    options={staff.map((user) => ({
                      value: userId(user),
                      label: userName(user),
                    }))}
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item
                name="severity"
                label="Severity"
                rules={[{ required: true }]}
              >
                <Select
                  options={SEVERITIES.map((s) => ({
                    value: s,
                    label: titleCase(s),
                  }))}
                  disabled={isClosedCaseEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Select
                  options={CASE_STATUSES.map((s) => ({
                    value: s,
                    label: titleCase(s),
                  }))}
                  disabled={isClosedCaseEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="acadyrID" label="Academic Year">
                <Select
                  allowClear
                  disabled={isClosedCaseEdit}
                  options={academicYears.map((y) => ({
                    value: y.id,
                    label: y.acadyr,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="termID" label="Term">
                <Select
                  allowClear
                  disabled={isClosedCaseEdit}
                  options={terms.map((t) => ({ value: t.id, label: t.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="classID" label="Class">
                <Select
                  allowClear
                  disabled={isClosedCaseEdit}
                  options={classes.map((c) => ({
                    value: c.id,
                    label: c.name || c.className,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="streamID" label="Stream">
                <Select
                  allowClear
                  disabled={isClosedCaseEdit}
                  options={streams.map((s) => ({ value: s.id, label: s.name }))}
                />
              </Form.Item>
            </Col>
            {isTerminalStatus && (
              <>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="finalDecisionByID"
                    label="Final Decision By"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select staff"
                      optionFilterProp="label"
                      options={staff.map((user) => ({
                        value: userId(user),
                        label: userName(user),
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={16}>
                  <Form.Item
                    name="finalNotes"
                    label="Final Notes"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Modal>

      <Drawer
        title={selectedCase?.caseNumber || "Case Details"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={900}
      >
        {selectedCase && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Title" span={2}>
                {selectedCase.title}
              </Descriptions.Item>
              <Descriptions.Item label="Subject">
                {selectedCase.subjectStudentName ||
                  selectedCase.subjectStaffName ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor(selectedCase.status)}>
                  {titleCase(selectedCase.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Severity">
                <Tag color={statusColor(selectedCase.severity)}>
                  {titleCase(selectedCase.severity)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Incident Date">
                {selectedCase.incidentDate}
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedCase.description}
              </Descriptions.Item>
            </Descriptions>

            <Tabs
              style={{ marginTop: 16 }}
              items={[
                {
                  key: "escalations",
                  label: "Escalations",
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Escalation steps cannot be deleted once added."
                      />
                      {!caseIsLocked && (
                        <Space wrap style={{ marginBottom: 12 }}>
                          <Button
                            icon={<PlusOutlined />}
                            onClick={() => {
                              escalationForm.resetFields();
                              setEscalationModalOpen(true);
                            }}
                          >
                            Add Step
                          </Button>
                          {selectedCase.isSubmitted &&
                            (selectedCase.escalationSteps || []).some(
                              (step) => step.isCurrent,
                            ) && (
                              <Button
                                icon={<RollbackOutlined />}
                                onClick={() => {
                                  sendBackForm.resetFields();
                                  setSendBackModalOpen(true);
                                }}
                              >
                                Send Back
                              </Button>
                            )}
                        </Space>
                      )}
                      <Table
                        size="small"
                        rowKey="id"
                        dataSource={selectedCase.escalationSteps || []}
                        columns={[
                          { title: "Order", dataIndex: "order" },
                          {
                            title: "From",
                            dataIndex: "fromRole",
                            render: titleCase,
                          },
                          {
                            title: "To",
                            dataIndex: "toRole",
                            render: titleCase,
                          },
                          {
                            title: "Direction",
                            dataIndex: "direction",
                            render: (v) =>
                              v ? (
                                <Tag
                                  color={v === "Returned" ? "orange" : "green"}
                                >
                                  {titleCase(v)}
                                </Tag>
                              ) : (
                                "-"
                              ),
                          },
                          {
                            title: "Handled By",
                            dataIndex: "handledByName",
                            render: (v) => v || "-",
                          },
                          {
                            title: "Status",
                            dataIndex: "isCurrent",
                            render: (v) => (
                              <Tag color={v ? "blue" : "default"}>
                                {v ? "Current" : "Completed"}
                              </Tag>
                            ),
                          },
                          {
                            title: "Actions",
                            render: (_, step) => (
                              <Space>
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<EyeOutlined />}
                                  onClick={() => setEscalationView(step)}
                                />
                                {step.isCurrent && !caseIsLocked && (
                                  <Button
                                    size="small"
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => openEscalationEdit(step)}
                                  />
                                )}
                                {step.isCurrent && !caseIsLocked && (
                                  <Button
                                    size="small"
                                    onClick={() => confirmResolveStep(step)}
                                  >
                                    Resolve
                                  </Button>
                                )}
                              </Space>
                            ),
                          },
                        ]}
                        pagination={false}
                      />
                    </>
                  ),
                },
                {
                  key: "evidence",
                  label: "Evidence",
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Evidence cannot be deleted once uploaded. Please review files before uploading."
                      />
                      {!caseIsLocked && (
                        <Button
                          icon={<UploadOutlined />}
                          style={{ marginBottom: 12 }}
                          onClick={() => {
                            evidenceForm.resetFields();
                            setEvidenceModalOpen(true);
                          }}
                        >
                          Upload Evidence
                        </Button>
                      )}
                      <Table
                        size="small"
                        rowKey="id"
                        dataSource={selectedCase.evidence || []}
                        columns={[
                          { title: "Title", dataIndex: "title" },
                          {
                            title: "File",
                            dataIndex: "file",
                            render: (v) =>
                              v ? (
                                <a
                                  href={
                                    /^https?:\/\//.test(v)
                                      ? v
                                      : `${Settings.backend}${v}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View / Download
                                </a>
                              ) : (
                                "-"
                              ),
                          },
                          { title: "Uploaded By", dataIndex: "uploadedByName" },
                          {
                            title: "Uploaded",
                            dataIndex: "uploadedAt",
                            render: (v) =>
                              v ? dayjs(v).format("YYYY-MM-DD") : "-",
                          },
                          {
                            title: "Notes",
                            dataIndex: "notes",
                            render: (v) => v || "-",
                          },
                        ]}
                        pagination={false}
                      />
                    </>
                  ),
                },
                {
                  key: "sanctions",
                  label: "Sanctions",
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Sanctions cannot be deleted once added."
                      />
                      {!caseIsLocked && (
                        <Space wrap style={{ marginBottom: 12 }}>
                          <Button
                            icon={<FileAddOutlined />}
                            onClick={() => {
                              sanctionForm.resetFields();
                              setSanctionModalOpen(true);
                            }}
                          >
                            Add Sanction
                          </Button>
                          <Button
                            onClick={async () => {
                              const ids = (selectedCase.sanctions || [])
                                .filter((s) => s.isApproved && !s.locked)
                                .map((s) => s.id);
                              if (ids.length) {
                                await bulkLockSanctions(ids);
                                refreshSelectedCase();
                              }
                            }}
                          >
                            Lock Approved
                          </Button>
                        </Space>
                      )}
                      <Table
                        size="small"
                        rowKey="id"
                        dataSource={selectedCase.sanctions || []}
                        columns={sanctionColumns}
                        pagination={false}
                      />
                    </>
                  ),
                },
              ]}
            />
          </>
        )}
      </Drawer>

      <Modal
        title="Bulk Escalate Cases"
        open={bulkEscalateOpen}
        onOk={confirmBulkEscalate}
        onCancel={() => setBulkEscalateOpen(false)}
        destroyOnClose
      >
        <Form form={bulkForm} layout="vertical">
          <Form.Item
            name="toStatus"
            label="Move To Status"
            rules={[{ required: true }]}
          >
            <Select
              options={CASE_STATUSES.map((s) => ({
                value: s,
                label: titleCase(s),
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Escalation Step"
        open={escalationModalOpen}
        onOk={submitEscalation}
        onCancel={() => setEscalationModalOpen(false)}
        destroyOnClose
      >
        <Form form={escalationForm} layout="vertical">
          <Form.Item
            name="fromRole"
            label="From Role"
            rules={[{ required: true }]}
          >
            <Select
              options={ROLES.map((r) => ({ value: r, label: titleCase(r) }))}
            />
          </Form.Item>
          <Form.Item name="toRole" label="To Role" rules={[{ required: true }]}>
            <Select
              options={ROLES.map((r) => ({ value: r, label: titleCase(r) }))}
            />
          </Form.Item>
          <Form.Item
            name="handledByID"
            label="Handled By"
            rules={[{ required: true, message: "Select a handler" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select staff"
              optionFilterProp="label"
              options={staff.map((user) => ({
                value: userId(user),
                label: userName(user),
              }))}
            />
          </Form.Item>
          <Form.Item name="actionTaken" label="Action Taken">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Escalation Step"
        open={!!escalationView}
        footer={null}
        onCancel={() => setEscalationView(null)}
        destroyOnClose
      >
        {escalationView && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Order">
              {escalationView.order || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="From Role">
              {escalationView.fromRole
                ? titleCase(escalationView.fromRole)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="To Role">
              {escalationView.toRole ? titleCase(escalationView.toRole) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Direction">
              {escalationView.direction
                ? titleCase(escalationView.direction)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Handled By">
              {escalationView.handledByName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Action Taken">
              {escalationView.actionTaken || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Notes">
              {escalationView.notes || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Return Reason">
              {escalationView.returnReason || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Escalated At">
              {escalationView.escalatedAt
                ? dayjs(escalationView.escalatedAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Completed At">
              {escalationView.resolvedAt
                ? dayjs(escalationView.resolvedAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title="Submit Discipline Case"
        open={!!submitModalRecord}
        onOk={confirmSubmit}
        onCancel={() => setSubmitModalRecord(null)}
        destroyOnClose
      >
        <Form form={submitForm} layout="vertical">
          <Form.Item
            name="toRole"
            label="To Role"
            rules={[{ required: true, message: "Select a role" }]}
          >
            <Select
              placeholder="Select role"
              onChange={loadSubmitHandlers}
              options={SUBMIT_TO_ROLES.map((role) => ({
                value: role.value,
                label: role.label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="handledByID"
            label="Handler"
            rules={[{ required: true, message: "Select a handler" }]}
          >
            <Select
              showSearch
              allowClear
              loading={submitHandlersLoading}
              placeholder="Select handler"
              optionFilterProp="label"
              options={submitHandlers.map((user) => ({
                value: userId(user),
                label: userName(user),
              }))}
              notFoundContent={
                submitHandlersLoading ? "Loading…" : "Select a role first"
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update Escalation Step"
        open={!!escalationEditStep}
        onOk={submitEscalationEdit}
        onCancel={() => setEscalationEditStep(null)}
        destroyOnClose
      >
        <Form form={escalationEditForm} layout="vertical">
          <Form.Item
            name="actionTaken"
            label="Action Taken"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Send Case Back"
        open={sendBackModalOpen}
        onOk={submitSendBack}
        onCancel={() => setSendBackModalOpen(false)}
        destroyOnClose
      >
        <Form form={sendBackForm} layout="vertical">
          <Form.Item
            name="returnReason"
            label="Return Reason"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="returnedToRole"
            label="Returned To Role"
            tooltip="Defaults to the role that escalated the case"
          >
            <Select
              allowClear
              options={ROLES.map((r) => ({ value: r, label: titleCase(r) }))}
            />
          </Form.Item>
          <Form.Item
            name="handledByID"
            label="Handled By"
            rules={[{ required: true, message: "Select a handler" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select staff"
              optionFilterProp="label"
              options={staff.map((user) => ({
                value: userId(user),
                label: userName(user),
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Upload Evidence"
        open={evidenceModalOpen}
        onOk={submitEvidence}
        onCancel={() => setEvidenceModalOpen(false)}
        destroyOnClose
      >
        <Form form={evidenceForm} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="file"
            label="File"
            valuePropName="fileList"
            getValueFromEvent={evidenceUploadValue}
            rules={[{ required: true }]}
          >
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Sanction"
        open={sanctionModalOpen}
        onOk={submitSanction}
        onCancel={() => setSanctionModalOpen(false)}
        destroyOnClose
      >
        <Form form={sanctionForm} layout="vertical">
          <Form.Item
            name="sanctionType"
            label="Sanction Type"
            rules={[{ required: true }]}
          >
            <Select
              options={SANCTION_TYPES.map((s) => ({
                value: s,
                label: titleCase(s),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="effectiveFrom" label="Effective From">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="effectiveTo" label="Effective To">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Set final severity to close"
        open={closeSeverityCases.length > 0}
        onOk={submitCloseSeverities}
        onCancel={() => setCloseSeverityCases([])}
        okText="Close with these severities"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="These cases cannot close without a final severity. Set one per case, then closing proceeds."
        />
        <Form form={closeSeverityForm} layout="vertical" preserve={false}>
          {closeSeverityCases.map((c) => (
            <Form.Item
              key={c.id}
              name={`severity_${c.id}`}
              label={`${c.title || `Case #${c.id}`} (${c.caseNumber || c.id})${c.finalSeverity ? ` — current: ${titleCase(c.finalSeverity)}` : ""}`}
              rules={[{ required: true, message: "Select severity" }]}
              initialValue={c.finalSeverity || undefined}
            >
              <Select placeholder="Select final severity">
                {SEVERITIES.map((s) => (
                  <Select.Option key={s} value={s}>
                    {titleCase(s)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ))}
        </Form>
      </Modal>

      <Modal
        title="Severity deductions"
        open={severityModalOpen}
        onCancel={() => setSeverityModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <span style={{ color: "#8c8c8c" }}>
            Points deducted per closed case at each severity. Overturned-on-appeal
            cases are excluded from deductions.
          </span>
          {severitySource && (
            <Tag color={severitySource === "stored" ? "green" : severitySource === "seeded" ? "blue" : "orange"}>
              Source: {severitySource}
            </Tag>
          )}
          {severityError && (
            <Alert type="error" showIcon message={severityError} closable onClose={() => setSeverityError(null)} />
          )}
          <Form form={severityForm} layout="vertical" preserve={false}>
            <Row gutter={12}>
              {SEVERITIES.map((level) => (
                <Col xs={12} md={6} key={level}>
                  <Form.Item
                    name={level}
                    label={titleCase(level)}
                    rules={[{ required: true, message: `Enter ${level} deduction` }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: "100%" }}
                      placeholder="0–100"
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
          <Space>
            <Button
              type="primary"
              loading={severityLoading}
              onClick={submitSeverityMap}
            >
              Save Map
            </Button>
            <Button onClick={loadSeverityMap} loading={severityLoading}>
              Reload
            </Button>
          </Space>
        </Space>
      </Modal>
    </div>
  );
};

export default DisciplineManagement;
